import apiV2 from '../api/v2';
import { toApiError } from './apiErrors';

// Accounts > Practice detail > Details > Beta access section, backed by
// /api/v2/practices/feature-flags (schema.practices.prisma's PracticeBetaFeature).

export interface PracticeBetaFeatureRow {
    practiceId: string;
    feature: string;
    enabled: boolean;
    enabledAt: string;
}

export interface KnownPracticeFeature {
    key: string;
    label: string;
}

// Features with a real gate, seeded here for a suggestion + friendly label.
// A new gated step should add its entry here, not a new file.
export const BILLING_FEATURE: KnownPracticeFeature = { key: 'billing', label: 'Billing' };
export const REFINEMENT_DESIGN_APPROVAL_FEATURE: KnownPracticeFeature = {
    key: 'refinement-design-approval',
    label: 'Refinement design approval'
};
export const KNOWN_PRACTICE_FEATURES: readonly KnownPracticeFeature[] = [
    BILLING_FEATURE,
    REFINEMENT_DESIGN_APPROVAL_FEATURE
];

export function knownFeatureLabel(key: string): string | undefined {
    return KNOWN_PRACTICE_FEATURES.find((f) => f.key === key)?.label;
}

/** Every enabled row across every practice - used only to build a deduped
 * suggestion list for AddBetaFeatureForm; never rendered directly. */
export async function fetchAllPracticeFeatureFlags(): Promise<PracticeBetaFeatureRow[]> {
    try {
        const response = await apiV2.get<{ success: boolean; data: PracticeBetaFeatureRow[] }>(
            '/practices/feature-flags'
        );
        return response.data.data;
    } catch (err) {
        throw toApiError(err);
    }
}

export async function fetchPracticeFeatureFlags(practiceId: string): Promise<string[]> {
    try {
        const response = await apiV2.get<{ success: boolean; data: string[] }>(
            `/practices/feature-flags/${practiceId}`
        );
        return response.data.data;
    } catch (err) {
        throw toApiError(err);
    }
}

/** Upserts the row - `enabled: true` adds/re-enables it, `enabled: false` disables it. */
export async function togglePracticeFeatureFlag(
    practiceId: string,
    feature: string,
    enabled: boolean
): Promise<void> {
    try {
        await apiV2.put(`/practices/feature-flags/${practiceId}`, { feature, enabled });
    } catch (err) {
        throw toApiError(err);
    }
}
